import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toMessage } from "@/api/errors";
import {
  createPasswordReset,
  getInviteLink,
  getPasswordResetLink,
  listFolders,
  listUsers,
  updateUser,
} from "@/api/lookups";
import type { Role, UserFolderMembership, UserListItem } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DataTable } from "@/components/table/DataTable";
import { InviteFormDialog } from "@/components/registries/InviteFormDialog";
import { UserFoldersDrawer } from "@/components/folder/UserFoldersDrawer";
import { useAuthStore } from "@/stores/auth";

const ROLES: Role[] = ["admin", "manager", "user"];

/** Max folder chips rendered inline before collapsing into a `+N` pill. */
const VISIBLE_FOLDER_CHIPS = 3;

/**
 * Copy a string to the clipboard with a synchronous textarea fallback for
 * insecure / non-clipboard contexts (plain http on an IP, jsdom in tests).
 */
async function copyToClipboard(value: string): Promise<boolean> {
  try {
    if (
      typeof window !== "undefined" &&
      window.isSecureContext &&
      navigator?.clipboard?.writeText !== undefined
    ) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // fall through to execCommand fallback
  }
  try {
    if (typeof document === "undefined") return false;
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

interface UsersFilters {
  folder_id: string;
  status: "" | "active" | "invited";
}

function readFiltersFromSearch(s: Record<string, unknown>): UsersFilters {
  return {
    folder_id: typeof s.folder_id === "string" ? s.folder_id : "",
    status: s.status === "active" || s.status === "invited" ? s.status : "",
  };
}

/**
 * Render the «Папки» cell for one user row.
 *
 *  * admin role  → dark `<все папки>` pill, no edit button.
 *  * invited row → grey static chip with «(будет назначено)» suffix.
 *  * 0 folders   → destructive «— нет доступа —» + «Дать доступ» button.
 *  * ≥ 1 folder  → first N chips (Variant A: flat chip per folder name;
 *    `is_default=true` rows are prefixed with `★`; the remainder is
 *    collapsed into a `+N` pill with the rest of the folder names in
 *    its native `title` tooltip).
 */
function FolderCell({
  row,
  onOpen,
}: {
  row: UserListItem;
  onOpen: (row: UserListItem) => void;
}) {
  const { t } = useTranslation();

  if (row.role === "admin") {
    return (
      <span
        className="inline-flex items-center rounded-full border border-slate-700 bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-100"
        title={t("adminUsers.folders.allFoldersTooltip")}
        data-testid={`user-folder-admin-pill-${row.id}`}
      >
        {t("adminUsers.folders.allFolders")}
      </span>
    );
  }

  if (row.status === "invited") {
    // TM-1ac64335 follow-up: show *which* folders the invite will grant so
    // the admin sees the access-to-be before the user registers. An invite
    // always carries ≥1 folder; the empty fallback only covers legacy /
    // pre-field mock rows.
    const inviteFolders = row.invite_folders ?? [];
    if (inviteFolders.length === 0) {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs text-muted-foreground"
          data-testid={`user-folder-invited-${row.id}`}
        >
          <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
            —
          </span>
          {t("adminUsers.folders.invitedSuffix")}
        </span>
      );
    }
    const visible = inviteFolders.slice(0, VISIBLE_FOLDER_CHIPS);
    const overflow = inviteFolders.slice(VISIBLE_FOLDER_CHIPS);
    return (
      <div
        className="flex flex-wrap items-center gap-1.5"
        data-testid={`user-folder-invited-${row.id}`}
      >
        {visible.map((f) => (
          <span
            key={f.id}
            className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
            title={f.name}
            data-testid={`invite-folder-chip-${row.id}-${f.id}`}
          >
            {f.name}
          </span>
        ))}
        {overflow.length > 0 ? (
          <span
            className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
            title={overflow.map((f) => f.name).join(", ")}
            data-testid={`invite-folder-overflow-${row.id}`}
          >
            {t("adminUsers.folders.overflow", { count: overflow.length })}
          </span>
        ) : null}
        <span
          className="text-xs italic text-muted-foreground"
          data-testid={`invite-folder-hint-${row.id}`}
        >
          {t("adminUsers.folders.invitedAccessHint")}
        </span>
      </div>
    );
  }

  const rowFolders = row.folders ?? [];
  if (rowFolders.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <span
          className="text-xs font-medium text-destructive"
          data-testid={`user-no-folders-${row.id}`}
        >
          {t("adminUsers.folders.noAccess")}
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid={`open-user-folders-${row.id}`}
          onClick={() => onOpen(row)}
        >
          {t("adminUsers.actions.grantAccess")}
        </Button>
      </div>
    );
  }

  const visible = rowFolders.slice(0, VISIBLE_FOLDER_CHIPS);
  const overflow = rowFolders.slice(VISIBLE_FOLDER_CHIPS);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map((m) => (
        <FolderChip key={m.id} userId={row.id} membership={m} />
      ))}
      {overflow.length > 0 ? (
        <span
          className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
          title={overflow.map((m) => m.name).join(", ")}
          data-testid={`user-folder-overflow-${row.id}`}
        >
          {t("adminUsers.folders.overflow", { count: overflow.length })}
        </span>
      ) : null}
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-6 px-2 text-xs"
        data-testid={`open-user-folders-${row.id}`}
        onClick={() => onOpen(row)}
      >
        {t("adminUsers.actions.editFolders")}
      </Button>
    </div>
  );
}

function FolderChip({
  userId,
  membership,
}: {
  userId: string;
  membership: UserFolderMembership;
}) {
  // Variant A (2026-05-22): no per-folder role chip styling. Every
  // membership renders as the same slate pill; `is_default=true`
  // folders get the leading `★` so the operator can still spot the
  // bootstrap folder at a glance.
  const classes =
    "inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200";
  return (
    <span
      className={classes}
      title={membership.name}
      data-testid={`user-folder-chip-${userId}-${membership.id}`}
    >
      {membership.is_default ? "★ " : ""}
      {membership.name}
    </span>
  );
}

/**
 * Renders the «Последний вход» cell as a human-readable relative date.
 *
 *  * `null` → `—` (covers invited rows + accepted-but-never-logged-in).
 *  * same day → `сегодня HH:mm`
 *  * else → `N дней назад` with Russian plural rules.
 */
function formatLastLogin(
  ts: string | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (ts === null || ts === undefined) return t("adminUsers.lastLogin.never");
  const then = new Date(ts);
  const now = new Date();
  const sameDay =
    then.getFullYear() === now.getFullYear() &&
    then.getMonth() === now.getMonth() &&
    then.getDate() === now.getDate();
  if (sameDay) {
    const hh = String(then.getHours()).padStart(2, "0");
    const mm = String(then.getMinutes()).padStart(2, "0");
    return t("adminUsers.lastLogin.today", { time: `${hh}:${mm}` });
  }
  const days = Math.max(
    1,
    Math.floor((now.getTime() - then.getTime()) / 86_400_000),
  );
  const mod10 = days % 10;
  const mod100 = days % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return t("adminUsers.lastLogin.daysAgoOne", { count: days });
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return t("adminUsers.lastLogin.daysAgoFew", { count: days });
  }
  return t("adminUsers.lastLogin.daysAgoMany", { count: days });
}

export function UsersAdmin() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.userId);
  // Filters are URL-backed so a deep-linked filter survives a refresh.
  // We deliberately use `useSearch({ strict: false })` so the page
  // works in tests / hosts where the route is not registered with
  // schema validation. The fallback is the empty filter.
  const rawSearch = useSearch({ strict: false });
  const navigate = useNavigate();
  const filters = useMemo(
    () => readFiltersFromSearch((rawSearch ?? {}) as Record<string, unknown>),
    [rawSearch],
  );
  const setFilter = (patch: Partial<UsersFilters>) => {
    void navigate({
      search: (prev: Record<string, unknown> | undefined) => ({
        ...(prev ?? {}),
        ...patch,
      }),
      replace: true,
    } as never);
  };

  // ---- data ----
  //
  // `listUsers()` currently has no query-param surface; the v2
  // `folder_id` filter is BE-side per the contract §1.6 but we
  // re-apply the same predicate client-side below so the page renders
  // the right subset immediately, and pass the filter through the
  // query key so a future helper signature change (with `?folder_id=…`
  // round-trip) refetches automatically. Variant A retired the
  // `folder_role` filter.
  const users = useQuery({
    queryKey: ["users", "list", { folder_id: filters.folder_id }],
    queryFn: () => listUsers(),
  });

  const folders = useQuery({
    queryKey: ["folders", "list"],
    queryFn: () => listFolders(),
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [drawerUser, setDrawerUser] = useState<UserListItem | null>(null);
  const [copied, setCopied] = useState<Set<string>>(new Set());
  const [copyFailed, setCopyFailed] = useState<Set<string>>(new Set());
  // Сброс пароля. Две стадии в одном окне: предупреждение, затем готовая
  // ссылка — чтобы живой токен не выдавался случайным нажатием в списке.
  const [resetTarget, setResetTarget] = useState<UserListItem | null>(null);
  const [resetStage, setResetStage] = useState<"confirm" | "ready">("confirm");
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [resetUntil, setResetUntil] = useState<string | null>(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetCopied, setResetCopied] = useState(false);
  // Client-side sort state for the «Папок» column.
  const [sortBy, setSortBy] = useState<"email" | "folders_count" | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // When the drawer is open and the cached user changes (e.g. after an
  // invalidate following a mutation), refresh the drawer's `user` prop
  // so its `folders[]` re-renders.
  useEffect(() => {
    if (drawerUser === null) return;
    const fresh = (users.data ?? []).find((u) => u.id === drawerUser.id);
    if (fresh !== undefined && fresh !== drawerUser) {
      setDrawerUser(fresh);
    }
  }, [users.data, drawerUser]);

  const update = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: { is_active?: boolean; role?: Role };
    }) => updateUser(id, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const handleCopyInviteLink = async (row: UserListItem) => {
    if (row.invite_id === undefined || row.invite_id === null) return;
    setCopyFailed((prev) => {
      const next = new Set(prev);
      next.delete(row.id);
      return next;
    });
    try {
      const { link } = await getInviteLink(row.invite_id);
      const ok = await copyToClipboard(link);
      if (ok) {
        setCopied((prev) => new Set(prev).add(row.id));
      } else {
        setCopyFailed((prev) => new Set(prev).add(row.id));
      }
    } catch {
      setCopyFailed((prev) => new Set(prev).add(row.id));
    }
  };

  // useCallback обязателен: функция уходит в зависимости useMemo, который
  // собирает колонки таблицы. Без него колонки пересобирались бы на каждый
  // рендер — eslint это и ловит (react-hooks/exhaustive-deps).
  const openReset = useCallback((row: UserListItem) => {
    setResetTarget(row);
    setResetStage("confirm");
    setResetLink(null);
    setResetUntil(null);
    setResetError(null);
    setResetCopied(false);
  }, []);

  const confirmReset = async () => {
    if (resetTarget === null) return;
    setResetBusy(true);
    setResetError(null);
    try {
      // Два вызова, а не один: выдача и показ живого токена — разные
      // события в аудите, и сервер намеренно не отдаёт ссылку сразу.
      const created = await createPasswordReset(resetTarget.id);
      const { link, expires_at } = await getPasswordResetLink(
        resetTarget.id,
        created.id,
      );
      setResetLink(link);
      setResetUntil(new Date(expires_at).toLocaleString("ru"));
      setResetStage("ready");
    } catch (err) {
      setResetError(toMessage(err));
    } finally {
      setResetBusy(false);
    }
  };

  const copyResetLink = async () => {
    if (resetLink === null) return;
    const ok = await copyToClipboard(resetLink);
    setResetCopied(ok);
    if (!ok) setResetError(t("adminUsers.passwordReset.copyFailed"));
  };

  // Apply client-side filters + sort on top of the BE result. The
  // status filter is FE-only (BE doesn't accept it in v2 §1.6); the
  // folder filters round-trip through `searchParams` and are also
  // applied client-side defensively so the page renders the right
  // subset before the refetch completes.
  const visibleRows = useMemo(() => {
    let rows = users.data ?? [];
    if (filters.status !== "") {
      rows = rows.filter((r) => (r.status ?? "active") === filters.status);
    }
    if (filters.folder_id !== "") {
      rows = rows.filter((r) =>
        (r.folders ?? []).some((f) => f.id === filters.folder_id),
      );
    }
    if (sortBy !== null) {
      const sorted = [...rows];
      sorted.sort((a, b) => {
        const av =
          sortBy === "folders_count"
            ? (a.folders_count ?? 0)
            : a.email.toLowerCase();
        const bv =
          sortBy === "folders_count"
            ? (b.folders_count ?? 0)
            : b.email.toLowerCase();
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
      rows = sorted;
    }
    return rows;
  }, [users.data, filters, sortBy, sortDir]);

  const columns: ColumnDef<UserListItem, unknown>[] = useMemo(
    () => [
      {
        id: "email",
        header: t("adminUsers.columns.email"),
        cell: (info) => {
          const row = info.row.original;
          const isInvited = row.status === "invited";
          return (
            <div className="flex items-center gap-2">
              <span>{row.email}</span>
              {isInvited ? (
                <span
                  className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300"
                  data-testid={`invited-badge-${row.id}`}
                >
                  {t("adminUsers.status.invited")}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "employee",
        header: t("adminUsers.columns.employee"),
        cell: (info) => {
          const display = info.row.original.employee_display;
          return display !== undefined && display !== null && display !== ""
            ? display
            : "—";
        },
      },
      {
        id: "role",
        header: t("adminUsers.columns.role"),
        cell: (info) => {
          const row = info.row.original;
          if (row.status === "invited") {
            return <span className="text-sm">{row.role}</span>;
          }
          // Last-admin guard: disable role dropdown for the currently
          // logged-in admin's own row so they can't lock themselves
          // out before any other admin exists.
          const isSelf =
            currentUserId !== null &&
            row.id === currentUserId &&
            row.role === "admin";
          return (
            <select
              aria-label={t("adminUsers.changeRole")}
              className="h-8 rounded-md border bg-background px-2 text-sm disabled:opacity-60"
              value={row.role}
              data-testid={`role-${row.id}`}
              disabled={isSelf || update.isPending}
              title={isSelf ? t("adminUsers.lastAdminTooltip") : undefined}
              onChange={(e) =>
                update.mutate({
                  id: row.id,
                  payload: { role: e.target.value as Role },
                })
              }
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          );
        },
      },
      {
        id: "folders",
        header: t("adminUsers.columns.folders"),
        cell: (info) => (
          <FolderCell
            row={info.row.original}
            onOpen={(row) => setDrawerUser(row)}
          />
        ),
      },
      {
        id: "folders_count",
        header: () => (
          <button
            type="button"
            className="font-medium hover:underline"
            data-testid="sort-folders-count"
            onClick={() => {
              if (sortBy === "folders_count") {
                setSortDir((d) => (d === "asc" ? "desc" : "asc"));
              } else {
                setSortBy("folders_count");
                setSortDir("desc");
              }
            }}
          >
            {t("adminUsers.columns.foldersCount")}
            {sortBy === "folders_count"
              ? sortDir === "asc"
                ? " ↑"
                : " ↓"
              : ""}
          </button>
        ),
        cell: (info) => (
          <span data-testid={`user-folders-count-${info.row.original.id}`}>
            {info.row.original.folders_count ?? 0}
          </span>
        ),
      },
      {
        id: "is_active",
        header: t("adminUsers.columns.isActive"),
        cell: (info) => {
          const row = info.row.original;
          if (row.status === "invited") return "—";
          return row.is_active ? t("common.yes") : t("common.no");
        },
      },
      {
        id: "last_login_at",
        header: t("adminUsers.columns.lastLogin"),
        cell: (info) => (
          <span data-testid={`user-last-login-${info.row.original.id}`}>
            {formatLastLogin(info.row.original.last_login_at, t)}
          </span>
        ),
      },
      {
        id: "created_at",
        header: t("adminUsers.columns.createdAt"),
        cell: (info) =>
          new Date(info.row.original.created_at).toLocaleString("ru"),
      },
      {
        id: "actions",
        header: "",
        cell: (info) => {
          const row = info.row.original;
          if (row.status === "invited") {
            const ok = copied.has(row.id);
            const failed = copyFailed.has(row.id);
            return (
              <div className="flex flex-col items-end gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid={`copy-invite-link-${row.id}`}
                  onClick={() => handleCopyInviteLink(row)}
                >
                  {ok
                    ? t("adminUsers.invite.copied")
                    : t("adminUsers.actions.copyInviteLink")}
                </Button>
                {failed ? (
                  <span
                    className="text-xs text-destructive"
                    role="alert"
                    data-testid={`copy-invite-error-${row.id}`}
                  >
                    {t("adminUsers.invite.copyFailed")}
                  </span>
                ) : null}
              </div>
            );
          }
          // Свой пароль так сбрасывать нельзя: сеансы гасятся при выдаче,
          // и администратор, закрывший окно со ссылкой, останется снаружи —
          // повторно запросить ссылку уже нечем. Смена своего пароля —
          // отдельная задача (страница профиля).
          const isSelf = row.id === currentUserId;
          const resetDisabled = !row.is_active || isSelf;
          const resetTitle = isSelf
            ? t("adminUsers.passwordReset.selfDisabled")
            : !row.is_active
              ? t("adminUsers.passwordReset.inactiveDisabled")
              : undefined;
          return (
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={resetDisabled}
                title={resetTitle}
                data-testid={`reset-password-${row.id}`}
                onClick={() => openReset(row)}
              >
                {t("adminUsers.actions.resetPassword")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={update.isPending}
                data-testid={`toggle-active-${row.id}`}
                onClick={() =>
                  update.mutate({
                    id: row.id,
                    payload: { is_active: !row.is_active },
                  })
                }
              >
                {row.is_active
                  ? t("adminUsers.deactivate")
                  : t("adminUsers.activate")}
              </Button>
            </div>
          );
        },
      },
    ],
    [t, update, copied, copyFailed, currentUserId, sortBy, sortDir, openReset],
  );

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-2">
        <h1 className="text-xl font-semibold">{t("adminUsers.title")}</h1>
        <Button
          type="button"
          onClick={() => setInviteOpen(true)}
          data-testid="open-invite-form"
        >
          {t("adminUsers.inviteButton")}
        </Button>
      </header>
      {/* Filter row — two URL-backed selects (folder, status). Variant A
          retired the "Роль в папке" filter. */}
      <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/30 p-2 text-sm">
        <label className="flex items-center gap-1">
          <span className="text-muted-foreground">
            {t("adminUsers.filters.folder")}
          </span>
          <select
            className="h-8 rounded-md border bg-background px-2"
            value={filters.folder_id}
            data-testid="users-filter-folder"
            onChange={(e) => setFilter({ folder_id: e.target.value })}
          >
            <option value="">{t("adminUsers.filters.anyFolder")}</option>
            {(folders.data ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.is_default ? "★ " : ""}
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1">
          <span className="text-muted-foreground">
            {t("adminUsers.filters.status")}
          </span>
          <select
            className="h-8 rounded-md border bg-background px-2"
            value={filters.status}
            data-testid="users-filter-status"
            onChange={(e) =>
              setFilter({
                status: (e.target.value as "" | "active" | "invited") ?? "",
              })
            }
          >
            <option value="">{t("adminUsers.filters.anyStatus")}</option>
            <option value="active">active</option>
            <option value="invited">{t("adminUsers.status.invited")}</option>
          </select>
        </label>
      </div>
      {update.isError ? (
        <p
          className="text-xs text-destructive"
          role="alert"
          data-testid="users-update-error"
        >
          {toMessage(update.error, t("adminUsers.errors.updateFailed"))}
        </p>
      ) : null}
      <DataTable
        data={visibleRows}
        columns={columns}
        testId="users-table"
        isLoading={users.isLoading}
      />
      <Dialog
        open={resetTarget !== null}
        onOpenChange={(open) => {
          if (!open) setResetTarget(null);
        }}
      >
        <DialogContent data-testid="password-reset-dialog">
          <DialogHeader>
            <DialogTitle>
              {resetStage === "confirm"
                ? t("adminUsers.passwordReset.title")
                : t("adminUsers.passwordReset.readyTitle")}
            </DialogTitle>
          </DialogHeader>
          {resetStage === "confirm" ? (
            <div className="space-y-3 text-sm">
              <dl className="space-y-1 rounded-md border bg-muted/30 p-3">
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">
                    {t("adminUsers.passwordReset.emailLabel")}
                  </dt>
                  <dd className="font-medium">{resetTarget?.email}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">
                    {t("adminUsers.passwordReset.ttlLabel")}
                  </dt>
                  <dd className="font-medium">
                    {t("adminUsers.passwordReset.ttlValue")}
                  </dd>
                </div>
              </dl>
              <p
                className="rounded-md border border-destructive/30 bg-destructive/10 p-3"
                data-testid="password-reset-warning"
              >
                {t("adminUsers.passwordReset.confirmWarning")}
              </p>
              {resetError !== null ? (
                <p className="text-destructive" role="alert">
                  {resetError}
                </p>
              ) : null}
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setResetTarget(null)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="button"
                  disabled={resetBusy}
                  data-testid="password-reset-confirm"
                  onClick={confirmReset}
                >
                  {t("adminUsers.passwordReset.confirmButton")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <p data-testid="password-reset-ready">
                {t("adminUsers.passwordReset.readyHint", {
                  until: resetUntil ?? "",
                })}
              </p>
              <div className="space-y-1">
                <span className="text-muted-foreground">
                  {t("adminUsers.passwordReset.linkLabel")}
                </span>
                <div className="flex items-stretch gap-2">
                  <code
                    className="flex-1 overflow-x-auto rounded-md border bg-muted/30 p-2 font-mono text-xs"
                    data-testid="password-reset-link"
                  >
                    {resetLink}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="password-reset-copy"
                    onClick={copyResetLink}
                  >
                    {resetCopied
                      ? t("adminUsers.passwordReset.copied")
                      : t("adminUsers.passwordReset.copy")}
                  </Button>
                </div>
              </div>
              {resetError !== null ? (
                <p className="text-destructive" role="alert">
                  {resetError}
                </p>
              ) : null}
              <div className="flex justify-end">
                <Button type="button" onClick={() => setResetTarget(null)}>
                  {t("adminUsers.passwordReset.done")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <InviteFormDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={() => {
          void queryClient.invalidateQueries({ queryKey: ["users"] });
        }}
      />
      <UserFoldersDrawer
        open={drawerUser !== null}
        user={drawerUser}
        onClose={() => setDrawerUser(null)}
      />
    </div>
  );
}
